import type { KotLine } from "@luckymenu/core";
import type { PrintDocument, StoredBlock } from "../document";
import { copyLabelBlock, dateAndTime, pair } from "./format";

/**
 * Kitchen order tickets (ux-design.md, Printed output): station name, the
 * table in double height, KOT number, time, waiter; items large with the
 * quantity first, variants, add-ons and notes indented. Cancelled items print
 * as a separate CANCEL ticket. Read from arm's length in a hot kitchen, so
 * the items are bold and double height.
 */

export type KotPrintData = {
	kotNo: number;
	stationName: string;
	/** Null for a takeaway, which prints its token number instead. */
	tableLabel: string | null;
	/** The daily order number; a takeaway's token. */
	orderNo: number;
	/** ISO time the KOT was made. */
	createdAt: string;
	timeZone: string;
	waiterName: string | null;
	lines: KotLine[];
	/** "Duplicate" on a reprint. */
	copyLabel?: string | null;
};

function where(data: KotPrintData): string {
	return data.tableLabel ? `TABLE ${data.tableLabel}` : `TAKEAWAY #${data.orderNo}`;
}

function lineBlocks(lines: KotLine[]): StoredBlock[] {
	const b: StoredBlock[] = [];
	const sub = (text: string): StoredBlock => ({
		t: "row",
		cols: [
			{ text: "", weight: 3 },
			{ text, weight: 21 },
		],
	});
	for (const line of lines) {
		b.push({
			t: "row",
			cols: [
				{ text: `${line.quantity} x`, weight: 3 },
				{ text: line.name, weight: 21 },
			],
			style: { bold: true, h: 2 },
		});
		if (line.variantName) b.push(sub(`(${line.variantName})`));
		for (const m of line.modifiers) b.push(sub(`+ ${m}`));
		if (line.note) b.push(sub(`>> ${line.note}`));
	}
	return b;
}

function ticket(data: KotPrintData, cancel: boolean): StoredBlock[] {
	const { time } = dateAndTime(data.createdAt, data.timeZone);
	const b: StoredBlock[] = [];
	if (cancel) {
		b.push({
			t: "text",
			text: "CANCEL",
			style: { align: "center", bold: true, invert: true, w: 2, h: 2 },
		});
	}
	b.push(...copyLabelBlock(data.copyLabel));
	b.push({ t: "text", text: data.stationName, style: { align: "center", bold: true } });
	b.push({ t: "text", text: where(data), style: { align: "center", bold: true, w: 2, h: 2 } });
	b.push(pair(`KOT #${data.kotNo}`, time, { bold: true }));
	b.push(pair(`Order #${data.orderNo}`, data.waiterName ? `By: ${data.waiterName}` : ""));
	b.push({ t: "rule", ch: "=" });
	if (cancel) b.push({ t: "text", text: "Do not make:", style: { bold: true } });
	b.push(...lineBlocks(data.lines));
	b.push({ t: "rule", ch: "=" });
	const qty = data.lines.reduce((n, l) => n + l.quantity, 0);
	b.push({
		t: "text",
		text: `${cancel ? "Cancelled" : "Items"}: ${data.lines.length}   Qty: ${qty}`,
		style: { bold: true },
	});
	b.push({ t: "beep", times: cancel ? 3 : 2 });
	b.push({ t: "cut" });
	return b;
}

/** A new KOT. */
export function kotDocument(data: KotPrintData): PrintDocument {
	return {
		version: 1,
		kind: "kot",
		copyLabel: data.copyLabel ?? null,
		blocks: ticket(data, false),
	};
}

/** A cancel KOT: the cancelled items, under CANCEL in reverse print. */
export function kotCancelDocument(data: KotPrintData): PrintDocument {
	return {
		version: 1,
		kind: "kotCancel",
		copyLabel: data.copyLabel ?? null,
		blocks: ticket(data, true),
	};
}
